import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Spin, message } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";
import { ApiManager, Locale } from "../../utils";
import StatCardGrid, { buildMoneyCard } from "./components/StatCardGrid";
import {
  buildStatisticsPermissionContext,
  getVisibleStatisticsSections,
} from "../../utils/helpers/statisticsPermissions";
import "./statisticsShared.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

function StatisticsOverview({ user, settings }) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const permissionContext = useMemo(
    () =>
      buildStatisticsPermissionContext({
        isProductEnabled: () => !!(settings && !settings.is_product_disabled),
        shopHasAgents: () => {
          if (settings && settings.disable_agent) {
            return false;
          }
          return !!(user?.merchant && user.merchant.has_agents);
        },
        isNewWechatTransferEnabled: () => !!(settings && settings.enabled_transfer_new),
      }),
    [user, settings]
  );

  const visibleSections = useMemo(
    () => getVisibleStatisticsSections(permissionContext),
    [permissionContext]
  );

  const fetchOverview = useCallback(async () => {
    if (!user?.token) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.statistics.overview.run({ token: user.token });
      if (res.result) {
        setData(res.data || null);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const orders = data?.orders || {};
  const products = data?.products || {};
  const commissions = data?.commissions || {};
  const transfers = data?.transfers || {};

  const sectionCards = {
    orders: [
      { key: "todayOrders", title: i18n.t("statisticsOverview.todayOrders"), value: orders.today?.orders_count ?? 0, suffix: i18n.t("statisticsOverview.orderUnit") },
      buildMoneyCard("todayIncome", i18n.t("statisticsOverview.todayIncome"), orders.today?.merchant_income_fen),
      { key: "monthOrders", title: i18n.t("statisticsOverview.monthOrders"), value: orders.this_month?.orders_count ?? 0, suffix: i18n.t("statisticsOverview.orderUnit") },
    ],
    products: [
      { key: "total", title: i18n.t("statisticsOverview.productTotal"), value: products.total ?? 0 },
      { key: "inSales", title: i18n.t("statisticsOverview.productInSales"), value: products.in_sales ?? 0 },
      { key: "stopSelling", title: i18n.t("statisticsOverview.productStopSelling"), value: products.stop_selling ?? 0 },
    ],
    commissions: [
      { key: "pending", title: i18n.t("statisticsOverview.commissionPending"), value: commissions.pending?.count ?? 0 },
      buildMoneyCard("pendingAmount", i18n.t("statisticsOverview.commissionPendingAmount"), commissions.pending?.amount),
      { key: "todayCount", title: i18n.t("statisticsOverview.commissionTodayCount"), value: commissions.today?.count ?? 0 },
      buildMoneyCard("todayAmount", i18n.t("statisticsOverview.commissionTodayAmount"), commissions.today?.amount),
    ],
    transfers: [
      { key: "todayCount", title: i18n.t("statisticsOverview.transferTodayCount"), value: transfers.today?.total_count ?? 0 },
      buildMoneyCard("todayAmount", i18n.t("statisticsOverview.transferTodayAmount"), transfers.today?.total_amount_fen),
      { key: "successCount", title: i18n.t("statisticsOverview.transferSuccessCount"), value: transfers.today?.success_count ?? 0 },
      { key: "failedCount", title: i18n.t("statisticsOverview.transferFailedCount"), value: transfers.today?.failed_count ?? 0 },
    ],
  };

  return (
    <div className="statistics-overview">
      <div className="statistics-overview__header">
        <h2>{i18n.t("statisticsOverview.title")}</h2>
      </div>

      <Spin spinning={loading}>
        {visibleSections.length === 0 ? (
          <Card>{i18n.t("statisticsOverview.noSections")}</Card>
        ) : (
          <Row gutter={[16, 16]} className="statistics-overview__nav-cards">
            {visibleSections.map((section) => (
              <Col key={section.key} xs={24} lg={12}>
                <Card
                  title={i18n.t(`statisticsOverview.${section.key}Title`)}
                  extra={<RightOutlined />}
                  onClick={() => history.push(section.path)}
                  hoverable
                >
                  <StatCardGrid items={sectionCards[section.key]} loading={loading} columns={2} />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Spin>
    </div>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
  settings: getMerchantSettings(state),
}))(StatisticsOverview);
